import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from '../config/axiosConfig';

const LanguageContext = createContext(null);

const STORAGE_KEY = 'preferred_language';

// Available languages
export const languages = {
  en: { name: 'English', nativeName: 'English', flag: 'GB' },
  de: { name: 'German', nativeName: 'Deutsch', flag: 'CH' },
  fr: { name: 'French', nativeName: 'Français', flag: 'CH' },
  it: { name: 'Italian', nativeName: 'Italiano', flag: 'CH' }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Check localStorage first, then browser language
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && languages[stored]) return stored;

    const browserLang = navigator.language?.split('-')[0];
    if (browserLang && languages[browserLang]) return browserLang;

    return 'en';
  });

  const [translations, setTranslations] = useState({});
  const [loading, setLoading] = useState(true);
  const [availableLanguages, setAvailableLanguages] = useState(['en', 'de', 'fr', 'it']);

  // Load translations
  const loadTranslations = useCallback(async (lang) => {
    try {
      setLoading(true);
      const response = await axios.get(`/settings/translations/${lang}`);
      setTranslations(response.data);
    } catch (error) {
      console.error('Failed to load translations:', error);
      // Fallback to English
      if (lang !== 'en') {
        try {
          const fallback = await axios.get('/settings/translations/en');
          setTranslations(fallback.data);
        } catch (e) {
          console.error('Failed to load fallback translations');
          setTranslations({});
        }
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Load app settings to get available languages
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const response = await axios.get('/settings');
        if (response.data.localization?.availableLanguages) {
          setAvailableLanguages(response.data.localization.availableLanguages);
        }
        if (response.data.localization?.defaultLanguage && !localStorage.getItem(STORAGE_KEY)) {
          setLanguageState(response.data.localization.defaultLanguage);
        }
      } catch (error) {
        console.error('Failed to load settings:', error);
      }
    };
    loadSettings();
  }, []);

  // Load translations when language changes
  useEffect(() => {
    loadTranslations(language);
  }, [language, loadTranslations]);

  // Set language and persist to localStorage
  const setLanguage = (lang) => {
    if (languages[lang]) {
      localStorage.setItem(STORAGE_KEY, lang);
      setLanguageState(lang);
    }
  };

  // Translation function with interpolation support
  const t = useCallback((key, params = {}) => {
    // Navigate nested keys like "auth.login"
    const keys = key.split('.');
    let value = translations;

    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Key not found, return the key itself
        return key;
      }
    }

    if (typeof value !== 'string') {
      return key;
    }

    // Interpolate {{param}} style placeholders
    return value.replace(/\{\{(\w+)\}\}/g, (_, paramKey) => {
      return params[paramKey] !== undefined ? params[paramKey] : `{{${paramKey}}}`;
    });
  }, [translations]);

  // Format date according to current locale
  const formatDate = useCallback((date, options = {}) => {
    const locale = language === 'en' ? 'en-US' :
                   language === 'de' ? 'de-CH' :
                   language === 'fr' ? 'fr-CH' :
                   language === 'it' ? 'it-CH' : 'en-US';

    try {
      return new Intl.DateTimeFormat(locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        ...options
      }).format(new Date(date));
    } catch {
      return date;
    }
  }, [language]);

  // Format relative time (e.g., "2 hours ago")
  const formatRelativeTime = useCallback((date) => {
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    const diffWeeks = Math.floor(diffMs / 604800000);
    const diffMonths = Math.floor(diffMs / 2592000000);

    if (diffMins < 1) return t('time.justNow');
    if (diffMins < 60) return t('time.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('time.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('time.daysAgo', { count: diffDays });
    if (diffWeeks < 4) return t('time.weeksAgo', { count: diffWeeks });
    return t('time.monthsAgo', { count: diffMonths });
  }, [t]);

  const value = {
    language,
    setLanguage,
    languages,
    availableLanguages,
    t,
    translations,   // raw bundle — needed for maps with dotted keys (e.g. findingContent.types["a.b.c"])
    loading,
    formatDate,
    formatRelativeTime
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

// Shorthand hook for translation function
export const useTranslation = () => {
  const { t, language } = useLanguage();
  return { t, language };
};

export default LanguageContext;
